import { ChangeEvent, useCallback, useMemo } from "react";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { TIME_FILTERS } from "../../Components/Filters/Hooks/useTimeFilterHook";
import {
  FilterValues,
  PageCandidateFilters,
  PageCandidatePagination,
  PaginationValues,
} from "../../Typings/Pagination";
import { ITimeFilter } from "../../Typings/SearchFilters";
import { createURI } from "../../Utilities/createURI";
import { STORAGE_VERSION_DATE } from "../../Utilities/browserStorage";

export const defaultCandidatesPagination = {
  page: 0,
  pageSize: 10,
  sortBy: "createdDate",
  sortDirection: "desc" as "asc" | "desc",
} as PaginationValues;

export const defaultCandidatesFilter = {
  search: "",
  tags: [],
  timeFilter: TIME_FILTERS["all-time-button"],
} as FilterValues;

export const candidatesFilterStore = create<PageCandidateFilters>()(
  persist(
    (set, get) =>
      ({
        setSearch: (key, search) => {
          const pageState = get()[key] ?? defaultCandidatesFilter;
          set((state) => ({ ...state, [key]: { ...pageState, search } }));
        },
        setTags: (key, tags) => {
          const pageState = get()[key] ?? defaultCandidatesFilter;
          set((state) => ({ ...state, [key]: { ...pageState, tags } }));
        },
        setTimeFilter: (key, timeFilter) => {
          const pageState = get()[key] ?? defaultCandidatesFilter;
          set((state) => ({ ...state, [key]: { ...pageState, timeFilter } }));
        },
      }) as PageCandidateFilters,
    {
      name: `candidates-filter-${STORAGE_VERSION_DATE}`,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !key.startsWith("set") && !key.startsWith("handle")),
        ),
    },
  ),
);

export const candidatesPaginationStore = create<PageCandidatePagination>()(
  persist(
    (set, get) =>
      ({
        setPage: (key, page) => {
          const pageState = get()[key] ?? defaultCandidatesPagination;
          set((state) => ({ ...state, [key]: { ...pageState, page } }));
        },
        setPageSize: (key, pageSize) => {
          const pageState = get()[key] ?? defaultCandidatesPagination;
          set((state) => ({ ...state, [key]: { ...pageState, pageSize, page: 0 } }));
        },
        setSortBy: (key, sortBy) => {
          const pageState = get()[key] ?? defaultCandidatesPagination;
          set((state) => {
            const isAsc = sortBy === pageState.sortBy && pageState.sortDirection === "asc";
            return {
              ...state,
              [key]: { ...pageState, sortBy, sortDirection: isAsc ? "desc" : "asc", page: 0 },
            };
          });
        },
        setSortDirection: (key, sortDirection) => {
          const pageState = get()[key] ?? defaultCandidatesPagination;
          set((state) => ({ ...state, [key]: { ...pageState, sortDirection } }));
        },
      }) as PageCandidatePagination,
    {
      name: `candidates-pagination-${STORAGE_VERSION_DATE}`,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) =>
        Object.fromEntries(
          Object.entries(state).filter(([key]) => !key.startsWith("set") && !key.startsWith("handle")),
        ),
    },
  ),
);

/**
 * Re-useable hook for managing pagination state for deep components, only includes pagination state
 *
 * Allows multiple components to use the same pagination store without conflicting with each other
 * @param key - The key to use in the saved session storage object *must be unique per component*
 * @returns the current state of the candidates filter store and the handlers to update the store
 */
export const useCandidatesPaginationStore = (key = "pagination") => {
  const { page, pageSize, sortBy, sortDirection, setPage, setPageSize, setSortBy } =
    candidatesPaginationStore((state) => {
      const keyState = state[key] || defaultCandidatesPagination;
      return {
        page: keyState.page,
        pageSize: keyState.pageSize,
        sortBy: keyState.sortBy,
        sortDirection: keyState.sortDirection,
        setPage: state.setPage,
        setPageSize: state.setPageSize,
        setSortBy: state.setSortBy,
      };
    });

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => setPage(key, newPage),
    [key, setPage],
  );
  const handleChangePageSize = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => setPageSize(key, parseInt(event.target.value, 10)),
    [key, setPageSize],
  );
  const handleRequestSort = useCallback(
    (property: string) => {
      setSortBy(key, property);
    },
    [key, setSortBy],
  );

  return {
    page,
    pageSize,
    sortBy,
    sortDirection,
    setPage,
    handleChangePage,
    handleChangePageSize,
    handleRequestSort,
  } as const;
};

/**
 * Re-useable hook for managing pagination state for deep components that include full filter state
 *
 * Allows multiple components to use the same pagination store without conflicting with each other
 * @param key - The key to use in the saved session storage object *must be unique per component*
 * @returns the current state of the candidates filter store and the handlers to update the store
 */
export const useCandidatesFilterStore = (key = "pagination") => {
  const { search, tags, timeFilter, setSearch, setTags, setTimeFilter } = candidatesFilterStore((state) => {
    const keyState = state[key] || defaultCandidatesFilter;
    return {
      search: keyState.search,
      tags: keyState.tags,
      timeFilter: keyState.timeFilter,
      setSearch: state.setSearch,
      setTags: state.setTags,
      setTimeFilter: state.setTimeFilter,
    };
  });
  const {
    page,
    pageSize,
    sortBy,
    sortDirection,
    setPage,
    handleChangePage,
    handleChangePageSize,
    handleRequestSort,
  } = useCandidatesPaginationStore(key);

  const getFilterQueryString = useCallback(
    (isPaginated = true, nextPage = false) => {
      const params = [] as string[][];

      if (isPaginated) {
        const newPage = nextPage ? page + 2 : page + 1;
        params.push(["page", newPage.toString()]);
        params.push(["pageSize", pageSize.toString()]);
        params.push(["sort", sortDirection]);
        params.push(["sortBy", sortBy]);
      }

      if (search) {
        params.push(["search", createURI(search)]);
      }

      if (timeFilter?.value) {
        params.push(["daysSubmitted", timeFilter.value.toString()]);
      }

      tags.forEach((tag) => params.push(["tags", createURI(tag)]));

      return params.length > 0 ? `?${params.map((param) => param.join("=")).join("&")}` : "";
    },
    [tags, timeFilter?.value, page, pageSize, search, sortDirection, sortBy],
  );

  const filterQueryString = useMemo(() => getFilterQueryString(), [getFilterQueryString]);
  const idsFilterQueryString = useMemo(() => getFilterQueryString(false), [getFilterQueryString]);
  const page2FilterQueryString = useMemo(() => getFilterQueryString(true, true), [getFilterQueryString]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(key, event.target.value);
    setPage(key, 0);
  };

  const handleTagsChange = (tags: string[]) => {
    setTags(key, tags);
    setPage(key, 0);
  };

  const handleTimeFilterChange = (timeFilter: ITimeFilter) => {
    setTimeFilter(key, timeFilter);
    setPage(key, 0);
  };

  const handleClearFilters = () => {
    setSearch(key, "");
    setTags(key, []);
    setTimeFilter(key, TIME_FILTERS["all-time-button"]);
    setPage(key, 0);
  };

  return {
    page,
    pageSize,
    sortBy,
    sortDirection,
    search,
    tags,
    timeFilter,
    filterQueryString,
    idsFilterQueryString,
    page2FilterQueryString,
    handleClearFilters,
    handleSearchChange,
    handleTagsChange,
    handleTimeFilterChange,
    handleChangePage,
    handleChangePageSize,
    handleRequestSort,
  } as const;
};

export type CandidatesFilterStore = ReturnType<typeof useCandidatesFilterStore>;
export type CandidatesPaginationStore = ReturnType<typeof useCandidatesPaginationStore>;
